import { useEffect } from 'react';
import type { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import cn from 'classnames';
import pickBy from 'lodash/pickBy';
import { QueryClient } from '@tanstack/react-query';
import { isAxiosError, STATUS_CODES } from '@y2/api-clients';
import { useUserDetails } from '@y2/auth-common/user-details-query';
import env from '@y2/environments';
import { useCollapse } from '@y2/hooks/use-collapse';
import { log } from '@y2/log';
import { EmailIcon } from '@y2/mango/components/icons';
import yup from '@y2/yup';

import styles from './[id].module.scss';
import texts from './[id].texts.json';
import { Container } from './temp/components/container/container';
import { Wrapper } from './temp/components/wrapper/wrapper';
import { CoverStaticMap } from './temp/features/cover-static-map/cover-static-map';
import { DealsHistory } from './temp/features/deals-history/deals-history';
import { DetailsList } from './temp/features/details-list/details-list';
import { LeadForm } from './temp/features/lead-form/lead-form';
import { SimilarProjects } from './temp/features/similar-projects/similar-projects';
import { SubHeader } from './temp/features/sub-header/sub-header';
import {
  getGrayProjectsOptions,
  type Project,
} from './temp/query-hooks/gray-projects';
import { buildAddress } from './temp/utils/build-address';
import { GeneralLayout } from '../../components/general-layout/general-layout';
import { MetaTags } from '../../components/meta-tags/meta-tags';
import { pushPageEvent } from '../../features/analytics/project-page';
import { useGetAddressMaster } from '../../query-hooks/address/use-address-master';
import { useYad1Projects } from '../../query-hooks/yad1-projects';
import { BadRequestError } from '../../services/errors/page-errors';

const PROJECTS_LIMIT = 20;

type Props = {
  project: Project;
  yad2Id: number;
};

const ProjectPage = ({ project, yad2Id }: Props) => {
  const router = useRouter();
  const { isCollapsed, collapse, expand } = useCollapse();
  const { data: user } = useUserDetails();
  const { data: address, isLoading } = useGetAddressMaster({
    city: project.city?.id,
    neighborhood: String(project.neighborhood?.id),
  });
  const { data: projectsRes } = useYad1Projects(
    pickBy({
      topArea: address?.top_area_id,
      area: address?.area_id,
      city: project.city?.id,
      neighborhood: project.neighborhood?.id,
      limit: PROJECTS_LIMIT,
    }),
    {
      enabled: Boolean(address?.area_id && address?.top_area_id),
    },
  );

  useEffect(() => {
    if (isLoading) return;
    pushPageEvent(user?.id);
  }, [isLoading, user?.id]);

  const similarProjects =
    projectsRes && projectsRes.projects?.length > 0
      ? projectsRes.projects
      : projectsRes?.similarProjects;

  return (
    <>
      <MetaTags
        metaTitle={texts.metaTitle}
        metaDescription={texts.metaDescription}
        canonicalUrl={`${env.routes.mainSite}${router.basePath}${router.pathname}`}
        robotContent="follow,index"
      />
      <GeneralLayout>
        <section className={styles.coverSection}>
          <SubHeader
            address={buildAddress(project)}
            projectName={project.projectName || ''}
            developerName={project.developers?.[0]?.name || ''}
          />
          <CoverStaticMap
            title={project.projectName || ''}
            lat={project.y || 0}
            lng={project.x || 0}
          />
        </section>
        <Wrapper className={styles.mainWrapper}>
          <Container>
            <div className={styles.pageContent}>
              <div className={styles.projectDetails}>
                <h2 className={styles.subHeading}>{texts.projectAbout}</h2>
                <DetailsList project={project} />
                <DealsHistory
                  project={project}
                  address={address}
                  headingSlot={
                    <h2 className={styles.subHeading}>
                      {texts.propertiesInTheArea}
                    </h2>
                  }
                />
              </div>
              <div
                className={cn(
                  styles.elevatorShaft,
                  !isCollapsed && styles.mobileOpen,
                )}
              >
                <div className={styles.elevator}>
                  <LeadForm
                    onClose={collapse}
                    id={yad2Id?.toString()}
                    similarProjects={similarProjects || []}
                  />
                </div>
              </div>
              <div className={cn(styles.mobileFormOpen, 'mobile-only')}>
                <button className={styles.openForm} onClick={expand}>
                  <EmailIcon />
                  {texts.contactUs}
                </button>
              </div>
            </div>
          </Container>
        </Wrapper>
        {similarProjects && similarProjects.length > 0 && (
          <Wrapper className={styles.secondaryWrapper}>
            <Container>
              <SimilarProjects similarProjects={similarProjects} />
            </Container>
          </Wrapper>
        )}
      </GeneralLayout>
    </>
  );
};

export default ProjectPage;

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  try {
    const querySchema = yup.object().shape({
      id: yup.number().positive(),
    });
    querySchema.validateSync(query);

    const { id } = query;
    const queryClient = new QueryClient();

    const {
      data: { data },
    } = await queryClient.fetchQuery(getGrayProjectsOptions(id as string));

    return {
      props: {
        project: data.data,
        yad2Id: data.yad2Id,
      },
    };
  } catch (error) {
    if (
      isAxiosError(error) &&
      error?.response?.status === STATUS_CODES.NOT_FOUND
    ) {
      return {
        notFound: true,
      };
    }
    if (error instanceof yup.ValidationError) {
      throw new BadRequestError(error.message);
    }
    log.error(error, 'failed to execute Page');

    throw error;
  }
};
